/**
 * Active-alarm summary card — compact, mobile-friendly active-alarm list on
 * the site dashboard.
 *
 * Renders directly off the live active-alarm snapshot already in
 * SiteDashboardLayoutContext (socket-fed, active-only) — no separate fetch.
 * Every snapshot row carries only an `alarm` entity ref {type,id,name}; the
 * alarm's label, category, severity, relay, and clearability resolve from the
 * reference catalog by that ref's id at render time, same as the full history
 * grid. The full history (all statuses, search, sort) lives at the routed
 * Alarm History page instead of an embedded grid.
 *
 * Contains:
 * - AlarmsSummaryCard — compact active-alarm list card (default export)
 */
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Box, Button, Card, IconButton, Tooltip, Typography } from '@mui/material';
import { green } from '@mui/material/colors';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { clearSiteAlarmState } from '@sophocles/api/odlApp';
import { useUser } from '@sophocles/app';
import BaseAlarmChip from '@sophocles/components/AlarmChip';
import { removeAlarmFromSnapshot } from '@sophocles/features/entities/site';
import { selectAlarmCategories, selectAlarmDefinitions } from '@sophocles/features/reference';
import { addToast } from '@sophocles/features/toast';
import { useAlarmTranslation } from '@sophocles/hooks/useAlarmTranslation';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';
import useSiteCommandWrite from '@sophocles/hooks/useSiteCommandWrite';
import { formatDateTime, formatDuration } from '@sophocles/utils/formatUtils';
import AdminOnlyAlarmBadge from '@sophocles/views/sites/components/alarms/AdminOnlyAlarmBadge';
import AlarmChip from '@sophocles/views/sites/components/alarms/AlarmChip';
import {
  facesForRelayNumber,
  timestampMs,
  translateAlarm,
  translateAlarmDescription,
} from '@sophocles/views/sites/components/alarms/alarmLabel';
import {
  isAlarmClearable,
  resolveTopCategory,
} from '@sophocles/views/sites/components/SiteDashboard/alarms/alarmUtils';
import { getAlarmStatus } from '@sophocles/views/sites/components/SiteDashboard/dashboardDerivations';

// Caps the card's own scroll region so a site with many active alarms doesn't
// push the rest of the dashboard down; the list scrolls internally past this.
const LIST_MAX_HEIGHT_PX = 340;

// Aggregate alarm status token → header-badge icon color / tint background,
// the same squircle language as the relay cards' power-state badge. 'good'
// (no active alarms) deliberately reads green rather than neutral.
const STATUS_BADGE_COLOR = {
  good: 'var(--status-ok)',
  bad: 'var(--status-bad)',
  warning: 'var(--status-warning)',
  purple: 'var(--status-attention)',
  info: 'var(--status-info)',
};
const STATUS_BADGE_TINT = {
  good: 'var(--status-ok-tint)',
  bad: 'var(--status-bad-tint)',
  warning: 'var(--status-warning-tint)',
  purple: 'var(--status-attention-tint)',
  info: 'var(--status-info-tint)',
};

/**
 * @param {object}   props
 * @param {object}   props.alarm            - active-alarm snapshot row ({ id, alarm: {type,id,name}, excessive, raised_at })
 * @param {object}   props.def              - alarm definition from the reference catalog (may be {})
 * @param {object}   props.category         - resolved top-level category (may be null)
 * @param {object[]} props.relays           - plain JS relay array from unit.relays
 * @param {object}   props.user             - viewing user, for timezone-aware date formatting
 * @param {boolean}  props.isStaff          - capabilities.is_staff
 * @param {boolean}  props.canClearAlarms   - capabilities.can_clear_alarms
 * @param {boolean}  props.commandLocked     - another device command owns the site
 * @param {*}        props.clearingId       - alarm id currently being cleared, or null
 * @param {Function} props.onClear          - fn(alarm, def) — invoked when the clear action is pressed
 */
function AlarmSummaryRow({
  alarm,
  def,
  category,
  relays,
  isStaff,
  canClearAlarms,
  commandLocked,
  clearingId,
  onClear,
}) {
  const { t } = useTranslation();
  const tAlarm = useAlarmTranslation();
  const { user } = useUser();
  const relayLoadTerm = useRelayLoadTerm();

  const categoryKey = category?.key ?? 'other';
  const clearable = isAlarmClearable(def, { excessive: alarm.excessive, isStaff, categoryKey });
  const canClear = clearable && canClearAlarms;
  const raisedMs = timestampMs(alarm.raised_at);
  const ageMs = raisedMs ? Date.now() - raisedMs : null;
  const faces = facesForRelayNumber(def.relay_number, relays, t, relayLoadTerm.singular);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        py: 'var(--space-2)',
        px: 'var(--space-3)',
        borderTop: '1px solid var(--md-outline-variant)',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, flex: '1 1 auto' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-1)', minWidth: 0 }}>
          <AlarmChip
            categoryKey={categoryKey}
            label={translateAlarm(tAlarm, def.key, def.display_name || def.key)}
            tAlarm={tAlarm}
            severity={def.severity}
            tooltip={translateAlarmDescription(tAlarm, def, category)}
          />
          {alarm.excessive ? (
            <Tooltip title={t('sites.excessiveAlarmsMessage')}>
              <BaseAlarmChip label={t('sites.excessive')} color="attention" />
            </Tooltip>
          ) : null}
          <AdminOnlyAlarmBadge def={def} />
        </Box>
        {faces ? (
          <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap title={faces}>
            {faces}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '2px',
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
          {formatDateTime(alarm.raised_at, user)}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
          {formatDuration(ageMs)}
        </Typography>
      </Box>

      {clearable ? (
        <Tooltip
          title={
            commandLocked
              ? t('commandActivity.commandsLocked', 'Wait for the queued command to finish before sending another.')
              : canClear
                ? t('actions.clear', 'Clear')
                : t('alarms.clearPermissionRequired', 'Requires clear permission')
          }
        >
          <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
            <IconButton
              size="small"
              aria-label={t('actions.clear', 'Clear')}
              disabled={!canClear || commandLocked || clearingId === alarm.id}
              onClick={() => onClear(alarm, def)}
              sx={{ color: 'text.primary' }}
            >
              <CloseOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>
      ) : null}
    </Box>
  );
}

AlarmSummaryRow.propTypes = {
  alarm: PropTypes.object.isRequired,
  def: PropTypes.object.isRequired,
  category: PropTypes.object,
  relays: PropTypes.arrayOf(PropTypes.object).isRequired,
  isStaff: PropTypes.bool.isRequired,
  canClearAlarms: PropTypes.bool.isRequired,
  commandLocked: PropTypes.bool.isRequired,
  clearingId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClear: PropTypes.func.isRequired,
};

/**
 * @param {object}        props
 * @param {object[]}      props.alarms       - live active-alarm snapshot rows ({ id, alarm, excessive, raised_at })
 * @param {object[]}      props.relays       - plain JS relay array from unit.relays
 * @param {number|string} props.siteId
 * @param {object}        props.capabilities - capabilities slice (is_staff, can_clear_alarms)
 * @param {string}        props.historyPath  - router path for the "View history" header action
 */
function AlarmsSummaryCard({ alarms, relays, siteId, capabilities, historyPath }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tAlarm = useAlarmTranslation();
  const alarmDefs = useSelector(selectAlarmDefinitions);
  const alarmCats = useSelector(selectAlarmCategories);
  const { commandLocked, runWrite } = useSiteCommandWrite(siteId);

  const defById = useMemo(() => new Map(alarmDefs.map(d => [d.id, d])), [alarmDefs]);
  const catById = useMemo(() => new Map(alarmCats.map(c => [c.id, c])), [alarmCats]);

  const isStaff = capabilities?.is_staff ?? false;
  const canClearAlarms = capabilities?.can_clear_alarms ?? false;

  const [clearingId, setClearingId] = useState(null);

  const handleClearAlarm = useCallback(
    async (alarm, def) => {
      setClearingId(alarm.id);
      try {
        const outcome = await runWrite(() => clearSiteAlarmState(siteId, alarm.id), { requiresCommand: true });
        if (!outcome.executed) return;
        dispatch(removeAlarmFromSnapshot(siteId, alarm.id));
        dispatch(
          addToast({
            title: t('general.success', 'Success'),
            message: `${translateAlarm(tAlarm, def.key, def.display_name || def.key)} ${t('alarms.cleared', 'cleared')}`,
            level: 'success',
            dismissible: true,
          }),
        );
      } catch {
        dispatch(
          addToast({
            title: t('general.error', 'Error'),
            message: t('alarms.clearFailed', 'Failed to clear alarm'),
            level: 'error',
            dismissible: true,
          }),
        );
      } finally {
        setClearingId(null);
      }
    },
    [dispatch, runWrite, siteId, t, tAlarm],
  );

  const rows = useMemo(
    () =>
      [...(alarms ?? [])]
        .sort((a, b) => timestampMs(b.raised_at) - timestampMs(a.raised_at))
        .map(alarm => ({
          alarm,
          def: defById.get(alarm.alarm?.id) ?? {},
          category: resolveTopCategory(catById, defById.get(alarm.alarm?.id)?.category_id),
        })),
    [alarms, defById, catById],
  );

  const alarmStatus = useMemo(() => getAlarmStatus(alarms ?? [], alarmDefs), [alarms, alarmDefs]);

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          // 12px header padding matches the relay card header so the header
          // controls land at the same offset on every dashboard card.
          px: 'var(--space-3)',
          py: 'var(--space-3)',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: 'var(--card-icon-radius)',
            bgcolor: STATUS_BADGE_TINT[alarmStatus] ?? STATUS_BADGE_TINT.warning,
            color: STATUS_BADGE_COLOR[alarmStatus] ?? STATUS_BADGE_COLOR.warning,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <NotificationsNoneOutlinedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
          {t('general.alarms')}
        </Typography>
        <Button size="small" endIcon={<ChevronRightIcon fontSize="small" />} onClick={() => navigate(historyPath)}>
          {t('general.history', 'History')}
        </Button>
      </Box>

      {rows.length > 0 ? (
        <Box sx={{ maxHeight: LIST_MAX_HEIGHT_PX, overflowY: 'auto' }}>
          {rows.map(({ alarm, def, category }) => (
            <AlarmSummaryRow
              key={alarm.id}
              alarm={alarm}
              def={def}
              category={category}
              relays={relays}
              isStaff={isStaff}
              canClearAlarms={canClearAlarms}
              commandLocked={commandLocked}
              clearingId={clearingId}
              onClear={handleClearAlarm}
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            px: 'var(--space-3)',
            py: 'var(--space-3)',
            borderTop: '1px solid var(--md-outline-variant)',
          }}
        >
          <TaskAltIcon sx={{ fontSize: 18, color: green[500], flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('alarms.allClear', 'All clear')}
          </Typography>
        </Box>
      )}
    </Card>
  );
}

AlarmsSummaryCard.propTypes = {
  alarms: PropTypes.arrayOf(PropTypes.object).isRequired,
  relays: PropTypes.arrayOf(PropTypes.object).isRequired,
  siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  capabilities: PropTypes.object,
  historyPath: PropTypes.string.isRequired,
};

export default AlarmsSummaryCard;
